import { APIError, fetcher, useApplicantAPI } from "@certn/api";
import { showToast } from "@certn/cascadia";
import {
  ButtonGroup,
  ContractScroll,
  ESignDocument,
  ESignSubmitControl,
  GhostButton,
  Loader,
  PrimaryButton,
  SectionMessage,
  Signature,
  Space,
  Stack,
  Text,
} from "@certn/cascadia-react";
import {
  CascadiaControlProps,
  CascadiaJsonFormContext,
  withCascadiaControlProps,
} from "@certn/forms";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import type { components } from "@certn/api/generated-schemas/applicant-portal";
import { ConsentRequiredModal } from "./ConsentRequiredModal";
import { ConsentsSectionMessage } from "./ConsentsSectionMessage";
import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { GeneralAuthorizationLayoutContext } from "../../layouts/GeneralAuthorizationLayoutContext";
import { useESignSubmit } from "../../esign/useESignSubmit";
import { createESignDownloadHandler } from "../../esign/createESignDownloadHandler";
import { CLICKWRAP_ACCESS_ID } from "../constants";

type GeneralAuthorizationConsent =
  components["schemas"]["GeneralAuthorizationTemplateView"];

export function GeneralAuthorizationControl(props: CascadiaControlProps) {
  const { handleChange, path, data } = props;
  const { t } = useTranslation();
  const { enableSuperdocApplicantAgreementTemplates } = useFlags();

  const { isSubmitted, submit } = useContext(CascadiaJsonFormContext);
  const { subLabel } = useContext(GeneralAuthorizationLayoutContext);

  const { caseId } = useApplicationFormContext();
  const { navigateToSection, prevSection, partiallySaveData } =
    useApplication();
  const [signing, setSigning] = useState(false);
  const [submittingSignature, setSubmittingSignature] = useState(false);

  const {
    data: consentData,
    isLoading,
    error,
  } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/general-authorization/` as "/api/internal/applicant/case/{short_id}/general-authorization/"
  );
  const consent = consentData as GeneralAuthorizationConsent | undefined;
  const [showConsentRequiredModal, setShowConsentRequiredModal] =
    useState(false);

  const [contractSigned, setContractSigned] = useState<boolean>(data);

  const { renderData, contractIDs, templateName, shouldUseESign, templateId } =
    useMemo(
      () => ({
        renderData: consent?.render_data,
        contractIDs: consent?.external_id ? [consent.external_id] : [],
        templateName: consent?.template_name,
        shouldUseESign:
          enableSuperdocApplicantAgreementTemplates && !!consent?.esign,
        templateId: consent?.id,
      }),
      [
        consent?.render_data,
        consent?.template_name,
        consent?.external_id,
        consent?.esign,
        consent?.id,
        enableSuperdocApplicantAgreementTemplates,
      ]
    );

  // ESignDocument renders an already-signed document read-only, so its onSubmit
  // never fires again on a return visit. Derive the signed state from the backend
  // instead of trusting the in-memory flag, otherwise an applicant who signed and
  // left before advancing is trapped behind ConsentRequiredModal with no way to
  // re-trigger the consent.
  const esignAlreadySigned = shouldUseESign && !!consent?.esign?.signedAt;
  const consentSigned = contractSigned || esignAlreadySigned;

  // throwErrors keeps SuperDoc interactive after a 400 (e.g. invalid PNG) so
  // the applicant can redraw and POST again without a full page reload.
  const { submitSignature } = useESignSubmit(caseId, {
    throwErrors: true,
    onSuccess: () => {
      setContractSigned(true);
      handleChange(path, true);
    },
    onFailure: (e) => {
      console.error("Error saving signature event (non-blocking)", e);
      Sentry.captureException(e, {
        tags: { component: "GeneralAuthorizationControl", action: "sign" },
        extra: { templateId, caseId },
      });
    },
  });
  // Legacy drawn-signature pad (NZ MOJ). Suppressed when SuperDoc eSign is active
  // because ESignDocument already embeds a signature field in the consent.
  const requiresSignature = consent?.requires_signature ?? false;
  const [signatureData, setSignatureData] = useState<string>("");
  const [signatureSubmitted, setSignatureSubmitted] = useState(
    consent?.signature_submitted ?? false
  );

  type SigningMode = "esign" | "legacy_pad" | "clickwrap";
  const signingMode: SigningMode = shouldUseESign
    ? "esign"
    : requiresSignature
      ? "legacy_pad"
      : "clickwrap";
  const showLegacySignaturePad = signingMode === "legacy_pad";
  const showConsentComplete =
    (showLegacySignaturePad && signatureSubmitted) ||
    (signingMode === "esign" && consentSigned);
  const isAwaitingESign = signingMode === "esign" && !consentSigned;
  const isAwaitingLegacySign =
    showLegacySignaturePad &&
    !signatureSubmitted &&
    (!signatureData || !consentSigned);
  const showSignButtonLabel =
    showLegacySignaturePad &&
    !signatureSubmitted &&
    consentSigned &&
    !!signatureData;
  const isNextDisabled =
    submittingSignature || signing || isAwaitingESign || isAwaitingLegacySign;

  // Sync signatureSubmitted when consent data loads (e.g. returning to this section)
  useEffect(() => {
    if (consent?.signature_submitted) {
      setSignatureSubmitted(true);
    }
  }, [consent?.signature_submitted]);

  // Seed the form value for an already-signed document so clicking "Next step"
  // submits the acceptance the applicant never got to persist.
  useEffect(() => {
    if (esignAlreadySigned && !contractSigned) {
      setContractSigned(true);
      handleChange(path, true);
    }
  }, [esignAlreadySigned, contractSigned, handleChange, path]);

  useEffect(() => {
    // The signed state is unknown until the consent request resolves. Opening the
    // modal on the loading render would strand an applicant who already submitted
    // the form once: nothing closes it once the signed state arrives.
    if (isLoading) return;

    if (isSubmitted && !consentSigned && !signing && !submittingSignature) {
      setShowConsentRequiredModal(true);
    }
  }, [isLoading, isSubmitted, consentSigned, signing, submittingSignature]);

  const handleSignatureChange = useCallback((signature: string) => {
    setSignatureData(signature);
  }, []);

  const handleSignatureClear = useCallback(() => {
    setSignatureData("");
  }, []);

  // Standard contract sign — unchanged, fires when checkbox is ticked
  const signContract = async () => {
    if (contractSigned) return;
    setContractSigned(true);

    try {
      setSigning(true);
      handleChange(path, true);
      await fetcher.submit(
        `/api/internal/applicant/case/${caseId}/sign-agreement/`,
        {
          render_data: renderData,
          external_id: consent?.external_id,
        }
      );
    } catch (e) {
      console.error("Error signing the general authorization document", e);
      Sentry.captureException(e);
      setShowConsentRequiredModal(true);
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
    } finally {
      setSigning(false);
    }
  };

  // Memoize the submit control component to prevent re-renders
  const SubmitControl = useMemo(
    () =>
      function ESignSubmitControlWrapper(
        submitControlProps: React.ComponentProps<typeof ESignSubmitControl>
      ) {
        return (
          <ESignSubmitControl
            {...submitControlProps}
            label={
              <Trans>
                I acknowledge and agree to the collection, use and disclosure of
                my personal information in accordance with the terms above.
              </Trans>
            }
          />
        );
      },
    []
  );
  // Submit the drawn signature for the general authorization consent.
  // Returns true on success, false on failure.
  const submitGeneralAuthorizationSignature = async (): Promise<boolean> => {
    if (!signatureData || signatureSubmitted) return false;

    setSubmittingSignature(true);
    try {
      await fetcher.submit(
        `/api/internal/applicant/case/${caseId}/general-authorization/sign/`,
        { applicant_signature: signatureData }
      );

      setSignatureSubmitted(true);
      handleChange(path, true);
      return true;
    } catch (e) {
      console.error("Error submitting general authorization signature", e);
      showToast(
        t("Something went wrong signing the consent form. Please try again."),
        { type: "error" }
      );
      Sentry.captureException(e);
      return false;
    } finally {
      setSubmittingSignature(false);
    }
  };

  const handleNextStep = async () => {
    if (showLegacySignaturePad && !signatureSubmitted) {
      if (!signatureData) {
        setShowConsentRequiredModal(true);
        return;
      }
      const success = await submitGeneralAuthorizationSignature();
      if (!success) return;
      // Don't auto-submit — let the user see "Consent Complete" and click "Next step"
      return;
    }
    submit();
  };

  const nextButtonText = showSignButtonLabel ? t("Sign") : t("Next step");

  if (isLoading) {
    return <Loader height="500px" />;
  }
  return (
    <Stack gap="space-4">
      <div className={styles.heading}>
        <Text as="h1" style="form/page-title" testID="consent-title">
          {templateName}
        </Text>

        {subLabel && (
          <Text
            style="form/body-primary"
            color="text-weak"
            testID="consent-sub-title"
          >
            {subLabel}
          </Text>
        )}
      </div>

      <ConsentsSectionMessage error={error} consents={[consent]} />
      {showConsentComplete ? (
        <SectionMessage
          icon="check_circle"
          type="success"
          sectionTitle={t("Consent Complete")}
        >
          {t(
            "Your consent has been successfully recorded with your signature."
          )}
        </SectionMessage>
      ) : (
        <Stack gap="space-4">
          <SectionMessage
            icon="verified_user"
            sectionTitle={t("Your privacy is important to us")}
            type="success"
          >
            {t(
              "Certn will only collect information that is needed to complete your check."
            )}
          </SectionMessage>
          {signingMode === "esign" && templateId ? (
            <ESignDocument
              style={esignDocumentStyle}
              data={consent?.esign}
              onSubmit={(d) => submitSignature(d, templateId)}
              onDownload={createESignDownloadHandler(caseId, templateId)}
              downloadFilename={t("General Authorization")}
              submitControl={SubmitControl}
            />
          ) : (
            <ContractScroll
              accessID={CLICKWRAP_ACCESS_ID}
              signerID={caseId}
              contractIDs={contractIDs}
              isSigned={data}
              onChange={() => signContract()}
              renderData={renderData}
              label={t(
                "I acknowledge and agree to the collection, use and disclosure of my personal information in accordance with the terms above."
              )}
            />
          )}

          {showLegacySignaturePad && !signatureSubmitted && (
            <Signature
              onChange={handleSignatureChange}
              onClear={handleSignatureClear}
              disabled={submittingSignature}
              width={580}
              height={200}
              className={styles.signature}
              description={t(
                "By adding my signature, I affirm my understanding and agreement to the document above."
              )}
            />
          )}
        </Stack>
      )}

      <div
        className={css(styles.actionBar, {
          borderTop: `1px solid var(--border-base)`,
        })}
      >
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              leftIcon="chevron_left"
              onPress={async () => {
                try {
                  await partiallySaveData();
                } catch (e) {
                  if (e instanceof APIError && e.status === 400) {
                    showToast(t("Error saving your progress"), {
                      type: "error",
                    });
                  } else {
                    showToast(t("Something went wrong, try again"), {
                      type: "error",
                    });
                    Sentry.captureException(e);
                  }
                }

                navigateToSection(prevSection);
              }}
              testID="section-back-button"
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />

          <PrimaryButton
            onPress={handleNextStep}
            variant="brand-primary"
            isLoading={signing || submittingSignature}
            isDisabled={isNextDisabled}
            testID="section-next-step-button"
          >
            {nextButtonText}
          </PrimaryButton>
        </ButtonGroup>
      </div>
      <ConsentRequiredModal
        show={showConsentRequiredModal}
        onClose={() => setShowConsentRequiredModal(false)}
        {...(showLegacySignaturePad &&
          !signatureData && {
            alternativeDialogContent: {
              header: t("Signature required"),
              content: (
                <Trans>
                  Please provide your signature using the signature pad before
                  continuing.
                </Trans>
              ),
            },
          })}
      />
    </Stack>
  );
}

// Static style object to prevent re-renders (defined outside component)
const esignDocumentStyle: React.CSSProperties = {
  width: "100%",
  height: "850px",
};

const styles = {
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  actionBar: css({
    paddingTop: `calc(${"var(--space-4)"} - 1px)`,
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
  }),
  signature: css({
    width: "100%",
    maxWidth: "100%",
  }),
};

GeneralAuthorizationControl.displayName = "GeneralAuthorizationControl";

export const GeneralAuthorizationControlRenderer = withCascadiaControlProps(
  GeneralAuthorizationControl
);
